import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import {
  faDownload,
  faPencil,
  faUpload,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Grid,
  Icon,
  Message,
  Modal,
  TextArea,
} from "@intility/bifrost-react";
import Box from "@intility/bifrost-react/Box";
import { useRef, useState } from "react";
import {
  planClientManifests,
  useApplyClientManifests,
  useClientManifestExport,
} from "../../../api/clientManifests";
import type {
  ClientManifest,
  ClientManifestOutcome,
  ClientManifestOutcomeKind,
  ClientManifestResult,
} from "../../../api/types";
import { CopyButton } from "../../../components/CopyButton";
import {
  downloadTextFile,
  editorRows,
  editorStyle,
  ManifestParseError,
  manifestsToYaml,
  yamlToManifests,
} from "../../../lib/manifestFile";

const OUTCOME_LABELS: Record<ClientManifestOutcomeKind, string> = {
  create: "Will create",
  update: "Will update",
  created: "Created",
  updated: "Updated",
  failed: "Failed",
  unchanged: "No changes",
  refused: "Refused",
};

function outcomeState(outcome: ClientManifestOutcomeKind) {
  switch (outcome) {
    case "create":
    case "update":
    case "created":
    case "updated":
      return "success";
    case "failed":
    case "refused":
      return "alert";
    default:
      return "neutral";
  }
}

export function OutcomeBadge({
  outcome,
}: {
  outcome: ClientManifestOutcomeKind;
}) {
  return (
    <Badge state={outcomeState(outcome)}>
      {OUTCOME_LABELS[outcome] ?? outcome}
    </Badge>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "(empty)";
  return typeof value === "string" ? value : JSON.stringify(value);
}

function OutcomeCard({ outcome }: { outcome: ClientManifestOutcome }) {
  return (
    <Box border radius padding={16}>
      <Grid gap={8}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <code>{outcome.clientId}</code>
          <OutcomeBadge outcome={outcome.outcome} />
        </div>
        {outcome.message && <p className="bf-p">{outcome.message}</p>}
        {outcome.changes.length > 0 && (
          <ul className="bf-p" style={{ margin: 0 }}>
            {outcome.changes.map((c) => (
              <li key={c.field}>
                <strong>{c.field}</strong>: {formatValue(c.from)} to{" "}
                {formatValue(c.to)}
              </li>
            ))}
          </ul>
        )}
        {outcome.gaps.length > 0 && (
          <Message state="warning" header="Some setup did not finish">
            <ul style={{ margin: 0 }}>
              {outcome.gaps.map((g) => (
                <li key={`${g.subject}-${g.message}`}>
                  {g.subject}: {g.message}
                </li>
              ))}
            </ul>
          </Message>
        )}
        {outcome.pendingApprovals.length > 0 && (
          <Message state="neutral" header="Waiting for approval">
            <ul style={{ margin: 0 }}>
              {outcome.pendingApprovals.map((p) => (
                <li key={p.requestId}>
                  Access to {p.audience} waits for the API owner.
                </li>
              ))}
            </ul>
          </Message>
        )}
      </Grid>
    </Box>
  );
}

// Per-client outcomes of a plan or an apply, plus the clients an apply leaves alone.
export function ManifestOutcomes({ result }: { result: ClientManifestResult }) {
  return (
    <Grid gap={12}>
      {result.clients.map((c) => (
        <OutcomeCard key={c.clientId} outcome={c} />
      ))}
      {result.untouched.length > 0 && (
        <p className="bf-p">
          Not in the manifest, so left as they are:{" "}
          {result.untouched.map((u, i) => (
            <span key={u}>
              {i > 0 && ", "}
              <code>{u}</code>
            </span>
          ))}
        </p>
      )}
    </Grid>
  );
}

function errorText(err: unknown) {
  return err instanceof Error ? err.message : "Something went wrong.";
}

export function ClientManifestTab({
  clientId,
  canEdit,
  inDrawer = false,
}: {
  clientId: string;
  canEdit: boolean;
  /** In a drawer: no heading, and the actions under the manifest. */
  inDrawer?: boolean;
}) {
  const exported = useClientManifestExport(clientId);
  const [copied, setCopied] = useState(false);
  const apply = useApplyClientManifests();
  const fileInput = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ClientManifest[] | null>(null);
  const [plan, setPlan] = useState<ClientManifestResult | null>(null);
  const [applied, setApplied] = useState<ClientManifestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [editing, setEditing] = useState(false);

  const currentYaml = exported.data
    ? manifestsToYaml(exported.data.manifests, exported.data.envVaryingFields)
    : "";
  const hasChanges =
    plan?.clients.some(
      (c) => c.outcome === "create" || c.outcome === "update",
    ) ?? false;

  async function handleCopy() {
    await navigator.clipboard.writeText(currentYaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleTextChange(value: string) {
    setText(value);
    setParsed(null);
    setPlan(null);
    setApplied(null);
    setError(null);
  }

  function handleStartEdit() {
    handleTextChange(currentYaml);
    setEditing(true);
  }

  function handleCancelEdit() {
    handleTextChange("");
    setEditing(false);
  }

  async function handleFile(file: File | undefined) {
    if (!file) return;
    handleTextChange(await file.text());
    if (fileInput.current) fileInput.current.value = "";
  }

  async function handlePreview() {
    setError(null);
    setApplied(null);
    setIsPlanning(true);
    try {
      const manifests = yamlToManifests(text);
      setParsed(manifests);
      setPlan(await planClientManifests(manifests));
    } catch (err) {
      setPlan(null);
      setError(
        err instanceof ManifestParseError ? err.message : errorText(err),
      );
    } finally {
      setIsPlanning(false);
    }
  }

  function handleApply() {
    if (!parsed) return;
    apply.mutate(parsed, {
      onSuccess: (result) => {
        setApplied(result);
        setPlan(null);
        setParsed(null);
        setShowConfirm(false);
        setEditing(false);
      },
    });
  }

  const confirmModal = (
    <Modal
      isOpen={showConfirm}
      onRequestClose={() => {
        if (apply.isPending) return;
        setShowConfirm(false);
        apply.reset();
      }}
      header="Save client changes"
      width={480}
    >
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          This changes the registration in this environment. Fields in the
          manifest replace the live values shown in the preview.
        </p>
        {apply.error && (
          <Message state="alert" header="Save failed">
            {errorText(apply.error)}
          </Message>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            state={apply.isPending ? "inactive" : undefined}
            onClick={
              apply.isPending
                ? undefined
                : () => {
                    setShowConfirm(false);
                    apply.reset();
                  }
            }
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            state={apply.isPending ? "inactive" : undefined}
            onClick={apply.isPending ? undefined : handleApply}
          >
            {apply.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </Grid>
    </Modal>
  );

  // The drawer's layout: the manifest box itself turns into the editor, and the
  // buttons under it change with the mode but keep their places: the primary
  // (filled) one at the far right (Copy, then Preview changes / Save changes),
  // the secondary one beside it (Download, then Upload file), and Edit at the
  // left (then Cancel).
  if (inDrawer) {
    const isEditing = canEdit && editing;
    return (
      <Grid gap={16}>
        <p className="bf-p">
          {isEditing
            ? "Edit the manifest or upload a file. Preview shows what would change. Nothing changes until you confirm."
            : "This is the live registration as a manifest. Use it to copy this client to another environment. It never holds secrets or access."}
        </p>
        {exported.isLoading && <p className="bf-p">Loading…</p>}
        {exported.error && (
          <Message state="alert" header="Could not load the manifest">
            {errorText(exported.error)}
          </Message>
        )}
        {isEditing ? (
          <TextArea
            label="Manifest"
            hideLabel
            value={text}
            rows={editorRows(text)}
            textAreaStyle={editorStyle}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="apiVersion: id.intility.com/v1"
          />
        ) : (
          currentYaml && (
            <Box border radius padding={16}>
              <pre style={{ margin: 0, overflow: "auto" }}>{currentYaml}</pre>
            </Box>
          )
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {isEditing ? (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".yaml,.yml,text/yaml"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button variant="basic" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                <Button
                  variant="basic"
                  onClick={() => fileInput.current?.click()}
                >
                  <Icon icon={faUpload} marginRight />
                  Upload file
                </Button>
                {plan && hasChanges ? (
                  <Button variant="filled" onClick={() => setShowConfirm(true)}>
                    Save changes
                  </Button>
                ) : (
                  <Button
                    variant="filled"
                    onClick={handlePreview}
                    state={isPlanning || !text.trim() ? "inactive" : undefined}
                  >
                    {isPlanning ? "Checking…" : "Preview changes"}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {canEdit && (
                <Button
                  variant="basic"
                  state={currentYaml ? undefined : "inactive"}
                  onClick={() => currentYaml && handleStartEdit()}
                >
                  <Icon icon={faPencil} marginRight />
                  Edit
                </Button>
              )}
              <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                <Button
                  variant="basic"
                  state={currentYaml ? undefined : "inactive"}
                  onClick={() =>
                    currentYaml &&
                    downloadTextFile(`${clientId}.yaml`, currentYaml)
                  }
                >
                  <Icon icon={faDownload} marginRight />
                  Download
                </Button>
                <Button
                  variant="filled"
                  state={currentYaml ? undefined : "inactive"}
                  onClick={() => currentYaml && handleCopy()}
                >
                  <Icon icon={copied ? faCheck : faCopy} marginRight />
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </>
          )}
        </div>
        {isEditing && error && (
          <Message state="alert" header="Could not preview the manifest">
            {error}
          </Message>
        )}
        {isEditing && plan && (
          <Grid gap={12}>
            <h4 className="bf-h4" style={{ margin: 0 }}>
              Preview
            </h4>
            <ManifestOutcomes result={plan} />
          </Grid>
        )}
        {applied && (
          <Grid gap={12}>
            <h4 className="bf-h4" style={{ margin: 0 }}>
              Result
            </h4>
            <ManifestOutcomes result={applied} />
          </Grid>
        )}
        {confirmModal}
      </Grid>
    );
  }

  return (
    <Grid gap={24}>
      <Grid gap={8}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 className="bf-h3" style={{ margin: 0, marginRight: "auto" }}>
            Client manifest
          </h3>
          {canEdit && !editing && (
            <Button
              variant="flat"
              small
              state={currentYaml ? undefined : "inactive"}
              onClick={() => currentYaml && handleStartEdit()}
            >
              <Icon icon={faPencil} marginRight />
              Edit
            </Button>
          )}
          <CopyButton value={currentYaml} />
          <Button
            variant="flat"
            small
            state={currentYaml ? undefined : "inactive"}
            onClick={() =>
              currentYaml && downloadTextFile(`${clientId}.yaml`, currentYaml)
            }
          >
            <Icon icon={faDownload} marginRight />
            Download
          </Button>
        </div>
        <p className="bf-p">
          This is the live registration as a manifest. Use it to copy this
          client to another environment. It never holds secrets or access.
        </p>
        {exported.isLoading && <p className="bf-p">Loading…</p>}
        {exported.error && (
          <Message state="alert" header="Could not load the manifest">
            {errorText(exported.error)}
          </Message>
        )}
        {!editing && currentYaml && (
          <Box border radius padding={16}>
            <pre style={{ margin: 0, overflow: "auto" }}>{currentYaml}</pre>
          </Box>
        )}
      </Grid>

      {canEdit && editing && (
        <Grid gap={12}>
          <p className="bf-p">
            Edit the manifest or upload a file. Preview shows what would change.
            Nothing changes until you confirm.
          </p>
          <TextArea
            label="Manifest"
            value={text}
            rows={editorRows(text)}
            textAreaStyle={editorStyle}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="apiVersion: id.intility.com/v1"
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              ref={fileInput}
              type="file"
              accept=".yaml,.yml,text/yaml"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              variant="filled"
              onClick={handlePreview}
              state={isPlanning || !text.trim() ? "inactive" : undefined}
            >
              {isPlanning ? "Checking…" : "Preview changes"}
            </Button>
            <Button variant="basic" onClick={() => fileInput.current?.click()}>
              <Icon icon={faUpload} marginRight />
              Upload file
            </Button>
            <Button variant="flat" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
          {error && (
            <Message state="alert" header="Could not preview the manifest">
              {error}
            </Message>
          )}
          {plan && (
            <Grid gap={12}>
              <h4 className="bf-h4" style={{ margin: 0 }}>
                Preview
              </h4>
              <ManifestOutcomes result={plan} />
              <div>
                <Button
                  variant="filled"
                  state={hasChanges ? undefined : "inactive"}
                  onClick={() => hasChanges && setShowConfirm(true)}
                >
                  Save changes
                </Button>
              </div>
            </Grid>
          )}
        </Grid>
      )}
      {applied && (
        <Grid gap={12}>
          <h4 className="bf-h4" style={{ margin: 0 }}>
            Result
          </h4>
          <ManifestOutcomes result={applied} />
        </Grid>
      )}

      {confirmModal}
    </Grid>
  );
}
